/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { MenuItem } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { TIME_FORMAT, TIMES } from '../../../../constants';
import { isTimeDisabled } from '../../../../utils/isTimeDisabled';
import { useTelegram } from '../../../../hooks/useTelegram';
import { StyledTextField } from '../../../common/StyledTextField/StyledTextField';
import { useAppSelector } from '../../../../hooks/useAppSelector';

import styles from './styles';
import { MeetingRoomUser } from '../../../../store/reducers/meetingRooms';

const isBetween = require('dayjs/plugin/isBetween');

dayjs.extend(isBetween);

interface EndTimePickerProps {
    setEndTimeValue: React.Dispatch<React.SetStateAction<Dayjs | null | undefined>>;
    endTimeValue: Dayjs | null | undefined;
    startTimeValue: Dayjs | null | undefined;
    chosenDateState?: string;
    setError: React.Dispatch<React.SetStateAction<string>>;
    endTimeError: string;
    editModeActive: boolean;
    checked: boolean;
    setChecked: React.Dispatch<React.SetStateAction<boolean>>;
    meetingRoomUsers: MeetingRoomUser[];
    disabled?: boolean;
    setIsEndTimeChanged: (isChanged: boolean) => void;
    isAdminRoute?: boolean;
    chosenUserTelegramId?: string;
}

function EndTimePicker({
    endTimeValue,
    setEndTimeValue,
    startTimeValue,
    chosenDateState,
    setError,
    endTimeError,
    editModeActive,
    checked,
    setChecked,
    meetingRoomUsers,
    disabled,
    setIsEndTimeChanged,
    isAdminRoute,
    chosenUserTelegramId,
}: EndTimePickerProps) {
    const { colorScheme, tgUserId } = useTelegram();

    const editedMeetingRoomBooking = useAppSelector(
        (store) => store.meetingRoomsReducer.editedUserMeetingRoom,
    );

    const todayDate = dayjs().format('YYYY-MM-DD');

    const allMeetingRoomUsers = useAppSelector(
        (store) => store.meetingRoomsReducer.allMeetingRoomUsers,
    ).filter((m) => {
        if (isAdminRoute) {
            if (editedMeetingRoomBooking) {
                return (
                    String(m.telegramUserId) === chosenUserTelegramId &&
                    m.userMeetingRoomInfo.id !== editedMeetingRoomBooking?.userMeetingRoomInfo.id
                );
            }
            if (chosenUserTelegramId) {
                return String(m.telegramUserId) === chosenUserTelegramId;
            }
        }
        return (
            Number(m.telegramUserId) === tgUserId &&
            m.userMeetingRoomInfo.id !== editedMeetingRoomBooking?.userMeetingRoomInfo.id
        );
    });

    useEffect(() => {
        if (endTimeError) {
            setEndTimeValue(null);
        }
    }, [endTimeError]);

    useEffect(() => {
        // We need this condition to check every time when we change startTime
        // if our current user has booked meeting rooms at the same time as our value in other rooms
        // If answer is yes, then we want to reset end time value,
        // because we don't want to allow specific user book several meeting rooms at the same time
        if (startTimeValue && endTimeValue) {
            const endTime = endTimeValue.format(TIME_FORMAT);

            const isDisabledInAllMeetingRooms = isTimeDisabled(
                endTime,
                allMeetingRoomUsers,
                true,
                startTimeValue,
            );

            if (isDisabledInAllMeetingRooms) {
                setEndTimeValue(null);
            }
        }
        // As an admin, we need this condition to check every time
        // when we change user (that we selected in dropdown) has booked meeting rooms
        // at the same time as our value in other meeting rooms.
        // If answer is yes, then we want to reset end time value,
        // because we do not want to allow specific user book several meeting rooms at the same time
        if (isAdminRoute && chosenUserTelegramId) {
            const isTimeShouldBeReset = allMeetingRoomUsers.some((booking) => {
                const formattedEndTime = dayjs(
                    booking.userMeetingRoomInfo.endTime,
                    TIME_FORMAT,
                ).format(TIME_FORMAT);
                return (
                    (String(booking.telegramUserId) === chosenUserTelegramId &&
                        formattedEndTime === endTimeValue?.format(TIME_FORMAT)) ||
                    // @ts-ignore
                    endTimeValue?.isBetween(
                        dayjs(`${todayDate} ${booking.userMeetingRoomInfo.startTime}`),
                        dayjs(`${todayDate} ${booking.userMeetingRoomInfo.endTime}`),
                        '[]',
                    )
                );
            });

            if (isTimeShouldBeReset) {
                setEndTimeValue(null);
            }
        }
    }, [startTimeValue, endTimeValue, chosenUserTelegramId, isAdminRoute, allMeetingRoomUsers]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const nowDay = dayjs().format('YYYY-MM-DD');

            const newEndTime = dayjs(`${nowDay} ${e.target.value}:00`);

            setEndTimeValue(newEndTime);
            setChecked(false);
            setIsEndTimeChanged(true);
        }
    };

    const key = 'startTime';

    const uniqueAllBookings = [
        ...new Map(
            allMeetingRoomUsers.map((item) => [item.userMeetingRoomInfo[key], item]),
        ).values(),
    ];

    const uniqueMeetingRoomBookings = [
        ...new Map(meetingRoomUsers.map((item) => [item.userMeetingRoomInfo[key], item])).values(),
    ];

    return (
        <div
            css={styles.timePickerWrapperStyles({
                colorScheme,
                isEdited: !!disabled,
            })}
        >
            <StyledTextField
                colorScheme={colorScheme}
                select
                label="End Time"
                value={endTimeValue?.format(TIME_FORMAT)}
                variant="outlined"
                disabled={disabled}
                onChange={handleChange}
                SelectProps={{
                    MenuProps: {
                        autoFocus: false,
                        PaperProps: styles.menuPaperStyles({ colorScheme }),
                    },
                }}
                InputLabelProps={{
                    className: 'inputLabel',
                    shrink: true,
                }}
            >
                {TIMES.map((t) => {
                    const isDisabled = isTimeDisabled(
                        t,
                        uniqueMeetingRoomBookings,
                        true,
                        startTimeValue,
                    );
                    const isDisabledInAllMeetingRoomUsers = isTimeDisabled(
                        t,
                        uniqueAllBookings,
                        true,
                        startTimeValue,
                    );

                    return (
                        <MenuItem
                            autoFocus={t === endTimeValue?.format(TIME_FORMAT)}
                            value={t}
                            disabled={isDisabled || isDisabledInAllMeetingRoomUsers}
                            key={t}
                            css={styles.menuItemStyles({ colorScheme })}
                        >
                            {t}
                        </MenuItem>
                    );
                })}
            </StyledTextField>
        </div>
    );
}

export default EndTimePicker;
